import { Injectable } from '@angular/core';
import { ExcelDataMapperService } from './excel-data-mapper.service';
import { ApplicationInsightsService } from '../application-insights/application-insights.service';
import { WOKRPAPER_SHEETS } from 'src/app/constants/workbook.constants';
import { SaveHeadersProvider } from './save-headers-provider.interface';

@Injectable({
  providedIn: 'root'
})
export class ReconDataService implements SaveHeadersProvider {

  constructor(
    private excelDataMapperService: ExcelDataMapperService,
    private appInsights: ApplicationInsightsService
  ) { }

  public getSaveHeaders(): string[] {
    return Object.keys(this.mapRecon);
  }

  public prepareReconSheetData(matrix: string[][]): { headers: string[], rows: (string | number | boolean)[][] } {
    this.appInsights.trackEvent('ReconDataService.prepareReconSheetData.called');
    try {
      const { sqlHeaders, tableRows } = this.excelDataMapperService.buildFilteredData(
        matrix,
        this.mapRecon,
        WOKRPAPER_SHEETS.ReconSheet,
        {
          skipRows: 0,
          removeLastRow: false,
          skipRowsWhenAllMappedColumnsBlank: true,
        }
      );

      tableRows.unshift(sqlHeaders);

      this.appInsights.trackEvent('ReconDataService.prepareReconSheetData.success', {
        headerCount: sqlHeaders.length,
        rowCount: tableRows.length
      });

      return { headers: sqlHeaders, rows: tableRows };
    } catch (error: any) {
      this.appInsights.trackException(error, { location: 'ReconDataService.prepareReconSheetData' });
      this.appInsights.trackEvent('ReconDataService.prepareReconSheetData.error', { error: error?.message || error });
      throw error;
    }
  }

  private mapRecon: Record<string, { sqlCol: string; dataType: string; required?: boolean }> = {
    "State": { sqlCol: "State", dataType: "string", required: true },
    "Vertex Company Code": { sqlCol: "VertexCompanyCode", dataType: "string", required: true },
    "Frequency": { sqlCol: "Frequency", dataType: "string", required: true },
    "Return Due": { sqlCol: "ReturnDue", dataType: "boolean", required: false },
    "Gross or Tax": { sqlCol: "GrossOrTax", dataType: "number", required: false },
    "Gross Difference": { sqlCol: "GrossDifference", dataType: "number", required: false },
    "Tax Difference": { sqlCol: "TaxDifference", dataType: "number", required: false },
    "Current Sales (Raw Data)": { sqlCol: "CurrentSales", dataType: "number", required: false },
    "Total Sales (Tax Return) Current month": { sqlCol: "TotalSalesCurrentMonth", dataType: "number", required: false },
    "Carry Forward Gross Amount (Raw Data)": { sqlCol: "CarryForwardGrossAmount", dataType: "number", required: false },
    "Total Sales (Raw Data)": { sqlCol: "TotalSalesRawData", dataType: "number", required: false },
    "Total Sales (Tax Return)": { sqlCol: "TotalSalesTaxReturn", dataType: "number", required: false },
    "Current Purchases (A/P & Inventory withdraw)": { sqlCol: "CurrentPurchases", dataType: "number", required: false },
    "Total Purchases (Tax Return) Current Month": { sqlCol: "TotalPurchasesCurrentMonth", dataType: "number", required: false },
    "Carry forward Purchases (A/P & Inventory withdraw)": { sqlCol: "CarryForwardPurchases", dataType: "number", required: false },
    "Total Purchases (Raw Data)": { sqlCol: "TotalPurchasesRawData", dataType: "number", required: false },
    "Total Purchases (Tax Return)": { sqlCol: "TotalPurchasesTaxReturn", dataType: "number", required: false },
    "Total Sales and Purchases (Raw Data)": { sqlCol: "TotalSalesAndPurchasesRawData", dataType: "number", required: false },
    "Total Sales and Purchases (Tax Return)": { sqlCol: "TotalSalesAndPurchasesTaxReturn", dataType: "number", required: false },
    "Gross Differences": { sqlCol: "GrossDifferences", dataType: "number", required: false },
    "Difference Explanation Sales": { sqlCol: "DifferenceExplanationSales", dataType: "string", required: false },
    "Notes Sales": { sqlCol: "NotesSales", dataType: "string", required: false },
    "Current Month Sales Tax Raw Data": { sqlCol: "CurrentMonthSalesTaxRawData", dataType: "number", required: false },
    "Carry Forward Sales Tax (Raw Data)": { sqlCol: "CarryForwardSalesTaxRawData", dataType: "number", required: false },
    "Total Sales Tax Raw Data": { sqlCol: "TotalSalesTaxRawData", dataType: "number", required: false },
    "B&O and Excise": { sqlCol: "BOAndExcise", dataType: "number", required: false },
    "Prepayments": { sqlCol: "Prepayments", dataType: "number", required: false },
    "Prepay Credit": { sqlCol: "PrepayCredit", dataType: "number", required: false },
    "Not Registered": { sqlCol: "NotRegistered", dataType: "number", required: false },
    "Credit Carry Forward": { sqlCol: "CreditCarryForward", dataType: "number", required: false },
    "Sales Tax to Tax Returns": { sqlCol: "SalesTaxToTaxReturns", dataType: "number", required: false },
    "Current Month Use Tax Raw Data": { sqlCol: "CurrentMonthUseTaxRawData", dataType: "number", required: false },
    "Carry Forward Use Tax (Raw Data)": { sqlCol: "CarryForwardUseTaxRawData", dataType: "number", required: false },
    "Total Use Tax Raw Data": { sqlCol: "TotalUseTaxRawData", dataType: "number", required: false },
    "Use Tax to Returns": { sqlCol: "UseTaxToReturns", dataType: "number", required: false },
    "Total Sales and Use Tax Raw Data": { sqlCol: "TotalSalesAndUseTaxRawData", dataType: "number", required: false },
    "Total SUT Tax Return (Before Discount)": { sqlCol: "TotalSUTTaxReturnBeforeDiscount", dataType: "number", required: false },
    "Tax Due": { sqlCol: "TaxDue", dataType: "number", required: false },
    "Current Month Sales Tax": { sqlCol: "CurrentMonthSalesTax", dataType: "number", required: false },
    "Current Month Use Tax": { sqlCol: "CurrentMonthUseTax", dataType: "number", required: false },
    "Discounts": { sqlCol: "Discounts", dataType: "number", required: false },
    "Current Month Carry Forward Tax": { sqlCol: "CurrentMonthCarryForwardTax", dataType: "number", required: false },
    "Difference to be Explained": { sqlCol: "DifferenceToBeExplained", dataType: "number", required: false },
    "Explained Tax Difference": { sqlCol: "ExplainedTaxDifference", dataType: "number", required: false },
    "Unexplained Tax Difference": { sqlCol: "UnexplainedTaxDifference", dataType: "number", required: false },
    "Difference Explanation": { sqlCol: "DifferenceExplanation", dataType: "string", required: false },
    "Notes": { sqlCol: "Notes", dataType: "string", required: false },
    "3 Month Average": { sqlCol: "ThreeMonthAverage", dataType: "number", required: false }
  };

}